import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { LoginModal } from './LoginModal'
import { useAuth } from '../context/AuthContext'

const IconHome = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
)

const IconUsers = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)

const IconBook = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
)

const IconTag = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
)

const IconPlus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
)

const IconSettings = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
)

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const { isLoggedIn, login, logout } = useAuth()
  const [showLoginModal, setShowLoginModal] = useState(false)

  const handleLoginSuccess = () => {
    login()
    setShowLoginModal(false)
  }

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <Link to="/" style={{ textDecoration: 'none', background: 'rgba(255,255,255,0.95)', borderRadius: '6px', padding: '4px 10px', display: 'flex', alignItems: 'center' }}>
            <img src="/logo.svg" alt="Marginalia" style={{ height: '44px', display: 'block' }} />
          </Link>

          {/* Desktop navigation */}
          <nav>
            <Link to="/" className={isActive('/') ? 'active' : ''}>Zitate</Link>
            <Link to="/authors" className={isActive('/authors') ? 'active' : ''}>Autoren</Link>
            <Link to="/sources" className={isActive('/sources') ? 'active' : ''}>Quellen</Link>
            <Link to="/tags" className={isActive('/tags') ? 'active' : ''}>Tags</Link>
            {isLoggedIn && (
              <Link to="/new" className={isActive('/new') ? 'active' : ''}>+ Neues Zitat</Link>
            )}
            {isLoggedIn && (
              <Link to="/review" className={isActive('/review') ? 'active' : ''}>Bearbeitungscenter</Link>
            )}
            {isLoggedIn && (
              <Link to="/settings" className={isActive('/settings') ? 'active' : ''}>Einstellungen</Link>
            )}
            {isLoggedIn ? (
              <button onClick={logout} className="logout-button">Logout</button>
            ) : (
              <button onClick={() => setShowLoginModal(true)} className="login-button">Login</button>
            )}
          </nav>

          {/* Mobile header: settings icon + login/logout */}
          <div className="header-mobile-actions">
            {isLoggedIn && (
              <Link to="/settings" className="header-icon-btn" aria-label="Einstellungen">
                <IconSettings />
              </Link>
            )}
            {isLoggedIn ? (
              <button onClick={logout} className="logout-button">Logout</button>
            ) : (
              <button onClick={() => setShowLoginModal(true)} className="login-button">Login</button>
            )}
          </div>
        </div>
      </header>

      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
          onLoginError={() => {}}
        />
      )}

      <main className="main">
        {isLoggedIn ? children : (
          <div style={{ textAlign: 'center', marginTop: '4rem', color: '#666' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Bitte melde dich an, um Marginalia zu nutzen.</p>
            <button onClick={() => setShowLoginModal(true)} className="login-button">Login</button>
          </div>
        )}
      </main>

      {/* Mobile bottom navigation */}
      <nav className="mobile-nav">
        <Link to="/" className={`mobile-nav-item${isActive('/') ? ' active' : ''}`}>
          <IconHome />
          <span>Zitate</span>
        </Link>
        <Link to="/authors" className={`mobile-nav-item${isActive('/authors') ? ' active' : ''}`}>
          <IconUsers />
          <span>Autoren</span>
        </Link>
        <Link to="/sources" className={`mobile-nav-item${isActive('/sources') ? ' active' : ''}`}>
          <IconBook />
          <span>Quellen</span>
        </Link>
        <Link to="/tags" className={`mobile-nav-item${isActive('/tags') ? ' active' : ''}`}>
          <IconTag />
          <span>Tags</span>
        </Link>
        {isLoggedIn && (
          <Link to="/new" className={`mobile-nav-item mobile-nav-new${isActive('/new') ? ' active' : ''}`}>
            <div className="mobile-nav-new-icon">
              <IconPlus />
            </div>
            <span>Neu</span>
          </Link>
        )}
      </nav>
    </div>
  )
}
